import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import SegmentedControl from 'components/kea/SegmentedControl';
import styles from './DeviceToolbar.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/**
 * DeviceToolbar - Reusable toolbar for screen and device selection
 * Used across all config steps (SearchBox, ResultsPage, Dropdown)
 */
const DeviceToolbar = ({ title, description, currentScreen, currentDevice, screens, onScreenChange, onDeviceChange, compact, disabled, leadingControls, trailingControls }) => {
  const t = useLocalizedMessage();

  return (
    <Box className={`${styles.toolbar} ${compact ? styles.compact : ''}`}>
      {(title || description) && (
        <Box className={styles.headerSection}>
          {title && <Typography variant="h6" className={styles.stepTitle}>{title}</Typography>}
          {description && <Typography variant="body2" className={styles.stepDescription}>{description}</Typography>}
        </Box>
      )}
      <Box className={styles.controlsSection}>
        {leadingControls}
        {onDeviceChange && (
          <Box className={styles.toolbarSection}>
            <Typography className={styles.toolbarLabel}>
              <IntlMessages id="ui.embedded.layout.configStepLayout.deviceToolbar.device" />
            </Typography>
            <SegmentedControl
              className={styles.deviceControl}
              value={currentDevice}
              onChange={onDeviceChange}
              label={t('search.editor.device.ariaLabel')}
              options={[
                {
                  value: 'desktop',
                  disabled,
                  label: <IntlMessages id="ui.embedded.layout.configStepLayout.deviceToolbar.desktop" />,
                  ariaLabel: t('ui.embedded.layout.configStepLayout.deviceToolbar.desktop'),
                  icon: <DesktopWindowsIcon fontSize="small" />,
                },
                {
                  value: 'mobile',
                  disabled,
                  label: <IntlMessages id="ui.embedded.layout.configStepLayout.deviceToolbar.mobile" />,
                  ariaLabel: t('ui.embedded.layout.configStepLayout.deviceToolbar.mobile'),
                  icon: <PhoneAndroidIcon fontSize="small" />,
                },
              ]}
            />
          </Box>
        )}

        {screens && screens.length > 1 && (
          <Box className={styles.toolbarSection}>
            <Typography className={styles.toolbarLabel}>
              <IntlMessages id="ui.embedded.layout.configStepLayout.deviceToolbar.screen" />
            </Typography>
            <SegmentedControl
              className={styles.screenControl}
              value={currentScreen}
              onChange={onScreenChange}
              label={t('search.editor.screen.ariaLabel')}
              options={screens.map(option => ({ ...option, disabled: disabled || option.disabled }))}
            />
          </Box>
        )}
        {trailingControls && <Box className={styles.trailingControls}>{trailingControls}</Box>}
      </Box>
    </Box>
  );
};

DeviceToolbar.propTypes = {
  leadingControls: PropTypes.node,
  trailingControls: PropTypes.node,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
  currentScreen: PropTypes.string,
  currentDevice: PropTypes.string,
  screens: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    }),
  ),
  onScreenChange: PropTypes.func,
  onDeviceChange: PropTypes.func,
  title: PropTypes.node,
  description: PropTypes.node,
};

DeviceToolbar.defaultProps = {
  compact: false,
  disabled: false,
  currentScreen: null,
  currentDevice: null,
  screens: null,
  onScreenChange: null,
  onDeviceChange: null,
  title: null,
  description: null,
};

export default DeviceToolbar;
